
import { CssBaseline, ThemeProvider as MuiThemeProvider, Theme, ThemeOptions, createTheme } from '@mui/material';
import { FC, useContext, useMemo } from 'react';

import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import { DefaultSpeciesThemes } from '../../../styles/defaults/DefaultSpeciesThemeOptions';
import { DefaultThemeOptions } from '../../../styles/defaults/DefaultThemeOptions';
import { IHasChildren } from '../../common/types/IHasChildren';

interface IDefaultThemeProviderProps extends IHasChildren {
    applySpeciesThemes?: boolean;
    themeOptions?: ThemeOptions;
    beforeThemeCreated?: (themeOptions: ThemeOptions) => ThemeOptions;
    afterThemeCreated?: (theme: Theme) => Theme;
}

const DefaultThemeProvider: FC<IDefaultThemeProviderProps> = ({
    afterThemeCreated,
    applySpeciesThemes,
    beforeThemeCreated,
    themeOptions, 
    children 
}) => {

    const { species: { species } } = useContext(SpeciesContext);

    const muiTheme = useMemo(() => {
        let options: ThemeOptions = !!themeOptions ? 
            {
                ...themeOptions,
                palette: {
                    ...themeOptions.palette,
                    mode: 'light',
                }
            } :
            {
                ...DefaultThemeOptions as ThemeOptions,
                palette: {
                    ...DefaultThemeOptions.palette,
                    mode: 'light',
                }
            };

        // Apply species themes

        if (applySpeciesThemes) {
            const currentTheme = DefaultSpeciesThemes[species];
    
            options.palette = {
                ...options.palette,
                primary: {
                    main: currentTheme.primaryColor
                },
                secondary: {
                    main: currentTheme.secondaryColor
                },
                background: {
                    default: currentTheme.backgroundColor
                }
            };
        }

        // Update MUI Theme Options using event function if provided.
        if (beforeThemeCreated) {
            options = beforeThemeCreated(options);
        }

        // Create MUI Theme from MUI Theme Options.
        let t = createTheme(options);

        // Update MUI Theme using event function if provided.
        if (afterThemeCreated) {
            t = afterThemeCreated(t);
        }

        return t;
    }, [themeOptions, afterThemeCreated, beforeThemeCreated, species]);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <CssBaseline enableColorScheme />
            {children}
        </MuiThemeProvider>
    );
}

export default DefaultThemeProvider